import { Link, useNavigate } from 'react-router-dom'
import { api } from '../api/client'
import type { Drip } from '../api/types'
import { Icon } from '../components/Icon'
import { ErrorState, Loading } from '../components/States'
import { relativeDays } from '../lib/format'
import { useAsync } from '../lib/useAsync'

export const DRIP_STATUS: Record<Drip['status'], { label: string; tone: string }> = {
  draft: { label: 'Draft', tone: 't-neutral' },
  active: { label: 'Running', tone: 't-positive' },
  paused: { label: 'Paused', tone: 't-warning' },
  stopped: { label: 'Stopped', tone: 't-neutral' },
}

/** Every drip, running ones first, each with the three numbers that say whether
 *  it's working: how many it has reached, how many replied, how many applied. */
export function DripCampaigns() {
  const { data, error, loading } = useAsync(() => api.drips(), [])
  const nav = useNavigate()

  if (loading && !data) return <div className="page"><Loading /></div>
  if (error || !data) return <div className="page"><ErrorState error={error} /></div>

  return (
    <div className="page campaign-page">
      <div className="page-head">
        <div>
          <h1 className="h1">Drip campaigns</h1>
          <p className="subline">
            A workflow sent to a whole group, a few people a day — past clients, leads who aren’t ready yet, everyone who got a mailer.
            It stops for anyone who replies or applies.
          </p>
          <p className="subline" style={{ marginTop: 2 }}>
            <Icon name="info" size={14} style={{ transform: 'translateY(2px)' }} /> A <strong>workflow</strong> is the schedule of
            steps, run for one person at a time. A <strong>drip campaign</strong> runs that same workflow for a whole list.
          </p>
        </div>
        <button className="btn primary" style={{ whiteSpace: 'nowrap', flex: 'none' }} onClick={() => nav('/drips/new')}>
          <Icon name="add" size={17} /> New drip campaign
        </button>
      </div>

      {data.messaging.mode === 'simulated' ? (
        <div className="notice warning" style={{ margin: '14px 0' }}>
          <Icon name="science" />
          <span>Messaging is in test mode: drips run and are recorded, but texts and emails aren’t delivered yet.</span>
        </div>
      ) : null}

      {data.drips.length === 0 ? (
        <div className="card wf-empty" style={{ marginTop: 16 }}>
          <div>
            <div style={{ fontWeight: 600 }}>No drip campaigns yet</div>
            <div className="quiet" style={{ fontSize: 12.5, marginTop: 2 }}>
              A good first one: past clients, with the Annual loan review workflow — it keeps you top of mind for refinances and referrals.
            </div>
          </div>
          <button className="btn" onClick={() => nav('/drips/new')}><Icon name="add" size={16} /> New drip campaign</button>
        </div>
      ) : (
        <div className="stack" style={{ marginTop: 16 }}>
          {data.drips.map((d) => {
            const s = DRIP_STATUS[d.status]
            return (
              <Link key={d.id} to={`/drips/${d.id}`} className="card wf-card" style={{ color: 'inherit', alignItems: 'center' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <span style={{ fontSize: 14, fontWeight: 600 }}>{d.name}</span>
                    <span className={`badge ${s.tone}`}>{s.label}</span>
                  </div>
                  <div className="quiet" style={{ fontSize: 12, marginTop: 3 }}>
                    {d.workflow_name} · {d.daily_limit} a day{d.auto_join ? ' · adds new matches' : ''}
                    {d.launched_at ? ` · launched ${relativeDays(d.launched_at)}` : ' · not launched'}
                  </div>
                </div>
                <div className="drip-mini">
                  <span><strong>{d.stats.people}</strong> people</span>
                  <span><strong>{d.stats.replied}</strong> replied</span>
                  <span><strong>{d.stats.applied}</strong> applied</span>
                </div>
                <Icon name="chevron_right" size={18} style={{ color: 'var(--ink-4)' }} />
              </Link>
            )
          })}
        </div>
      )}
    </div>
  )
}
